import { apiClient } from "./apiClient";
import { resolveMediaUrl } from "./mediaUrl";

export interface UploadResult {
  url: string;
  path: string;
  error?: string;
}

const getUploadUrl = (path: string) => {
  const raw = String(path || "").trim();
  if (!raw) {
    return "";
  }

  if (
    raw.startsWith("http://") ||
    raw.startsWith("https://") ||
    raw.startsWith("data:") ||
    raw.startsWith("blob:")
  ) {
    return raw;
  }

  const uploadPath = raw.startsWith("/") ? raw : `/uploads/${raw}`;
  return resolveMediaUrl(uploadPath);
};

export const storageService = {
  async uploadFile(file: File, _bucket: string = "uploads", _folder?: string): Promise<UploadResult> {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const headers: HeadersInit = {};
      const token = apiClient.getAuthToken();
      if (token) {
        (headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }

      const response = await fetch(apiClient.toApiUrl("/uploads/image"), {
        method: "POST",
        body: formData,
        headers,
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || "Upload failed");
      }

      return {
        url: getUploadUrl(payload.url),
        path: payload.path,
      };
    } catch (error) {
      return {
        url: "",
        path: "",
        error: error instanceof Error ? error.message : "Upload failed",
      };
    }
  },

  async deleteFile(pathOrUrl: string, _bucket: string = "uploads"): Promise<{ error?: string }> {
    try {
      await apiClient.delete<void>("/uploads/image", {
        url: pathOrUrl,
      });

      return {};
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : "Delete failed",
      };
    }
  },

  getPublicUrl(path: string, _bucket: string = "uploads"): string {
    return getUploadUrl(path);
  },

  async uploadNewsImage(file: File): Promise<UploadResult> {
    return this.uploadFile(file, "uploads", "news");
  },

  async uploadIpoLogo(file: File): Promise<UploadResult> {
    return this.uploadFile(file, "uploads", "ipos");
  },

  async uploadJobBanner(file: File): Promise<UploadResult> {
    return this.uploadFile(file, "uploads", "jobs");
  },

  async uploadAffiliateBanner(file: File): Promise<UploadResult> {
    return this.uploadFile(file, "uploads", "affiliates");
  },

  async uploadUserAvatar(file: File, _userId: string): Promise<UploadResult> {
    return this.uploadFile(file, "uploads", "avatars");
  },

  async listFiles(_folder: string, _bucket: string = "uploads"): Promise<{ data: any[] | null; error?: string }> {
    try {
      const data = await apiClient.get<any[]>("/uploads/list", { folder: _folder || "images" });
      return { data };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : "List failed",
      };
    }
  },
};
